/**
 * The fan-site notice, in both languages. One home for it: the page footer
 * and every transactional email read it from here, so they cannot drift apart.
 *
 * It is one sentence run in three parts: what the site is, who it is not
 * connected to, and who owns the rights. The middle part is the only one that
 * changes. On a site page, and in every email, it is the general clause below.
 * On a world's pages the world's own clause — naming its author, estate and
 * publishers, from that world's row in the database — takes its place, so a
 * page about one world never names another's estate, and an account message,
 * which is not about any world, names none.
 */

import type { Locale } from './locale.ts'

const OPENING: Record<Locale, string> = {
  bg: 'Неофициален фен сайт за книги.',
  en: 'Unofficial fan site about books.',
}

/** Who the site is not connected to, when the page is not about one world. */
export const GENERAL_AFFILIATION: Record<Locale, string> = {
  bg: 'Не е свързан с авторите, наследниците им или издателите.',
  en: 'Not affiliated with the authors, their estates or the publishers.',
}

const CLOSING: Record<Locale, string> = {
  bg: 'Всички права върху произведенията принадлежат на техните носители.',
  en: 'All rights in the works belong to their holders.',
}

/**
 * Ends the clause with exactly one full stop, whether or not it came with one.
 * The clause comes from a database row, and "издател.. Всички" is what joining
 * a clause that already ends in one looks like.
 */
function asSentence(clause: string): string {
  return `${clause.trim().replace(/\.+$/, '')}.`
}

/** The notice with a world's clause in the middle, or the general one. */
export function siteNotice(locale: Locale, affiliation: string = GENERAL_AFFILIATION[locale]): string {
  return [OPENING[locale], asSentence(affiliation), CLOSING[locale]].join(' ')
}

/** The notice on site pages and in every email. */
export const SITE_NOTICE: Record<Locale, string> = {
  bg: siteNotice('bg'),
  en: siteNotice('en'),
}
